
import Vue from 'vue';
import { mapState } from 'vuex';
import api from '@/api';
import { ExcludedPractice } from '@/store/modules/settings/settings';
import BannerStandModal from '@/components/layout/banner-stand-modal.vue';

export default Vue.extend({
  name: 'BannerstandBannerWidget',
  components: {
    BannerStandModal,
  },
  data() {
    return {
      hasAvailableProducts: false,
      isLoading: true,
      showModal: false,
    };
  },
  computed: {
    ...mapState('user/profile', {
      currentUser: 'currentProfile',
    }),
    ...mapState('settings', ['settings']),

    hasPractice(): boolean {
      return this.$store.getters['user/profile/hasPractice'];
    },

    isInNorthAmerica(): boolean {
      return this.$store.getters['user/profile/isInNorthAmerica'];
    },

    bannerStandExcludedPractices(): ExcludedPractice[] {
      return this.settings?.bannerStandExcludedPractices || [];
    },

    isPracticeExcluded(): boolean {
      const practiceId = this.currentUser?.practiceId;
      if (!practiceId) return false;
      return this.bannerStandExcludedPractices.some((p) => p.id === practiceId);
    },

    isFeaturedUser(): boolean {
      return Boolean(this.currentUser?.featuredUser);
    },

    shouldShowBanner(): boolean {
      return (
        this.isFeaturedUser &&
        this.isInNorthAmerica &&
        this.hasPractice &&
        !this.isPracticeExcluded &&
        !this.isLoading &&
        this.hasAvailableProducts
      );
    },
  },
  methods: {
    openModal() {
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      // Re-check available products in case user ordered something
      this.checkAvailableProducts();
    },
    async checkAvailableProducts() {
      if (!this.hasPractice || !this.isInNorthAmerica || this.isPracticeExcluded) {
        this.isLoading = false;
        return;
      }

      try {
        // Get user language from profile
        const languageCode = this.currentUser?.languageCode || 'en';

        // Get products that have banner stand images for this language (with ordered info)
        const usedProductsResponse = await api.bannerStandImages.getUsedProducts({
          language_code: languageCode,
          include_ordered: true,
        });

        // Handle response format - can be array or object with products/ordered
        let usedProductIds: number[];
        let orderedProductIds: number[] = [];
        if (Array.isArray(usedProductsResponse.data)) {
          usedProductIds = usedProductsResponse.data;
        } else {
          usedProductIds = usedProductsResponse.data.products || [];
          orderedProductIds = usedProductsResponse.data.ordered || [];
        }

        // Get practice-owned product IDs from user profile
        const practiceProducts = this.currentUser?.practice?.products || {};
        const practiceProductIds = Object.keys(practiceProducts).map((id) => Number(id));

        // Check if there are any products that:
        // 1. Have bannerstand images for this language
        // 2. Are owned by the practice
        // 3. Have NOT been already ordered
        this.hasAvailableProducts = usedProductIds.some((productId) =>
          practiceProductIds.includes(productId) && !orderedProductIds.includes(productId)
        );
      } catch (error) {
        this.hasAvailableProducts = false;
      } finally {
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.checkAvailableProducts();
  },
});
